import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import { AchieveClient } from '../../helpers/client';
import { Linear } from '../progressBars/linear';
import { NavBar } from '../appBars/navBar/navBar';
import { AchievementItem } from './achievementItem';
import { useTranslation } from 'react-i18next';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { AchievementsRoute } from '../../routes/allRoutes';
import PeopleIcon from '@mui/icons-material/People';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import LocalLaundryServiceOutlinedIcon from '@mui/icons-material/LocalLaundryServiceOutlined';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export enum AchievementType {
  Stores = 'stores',
  SmartWardrobes = 'smartDrobes',
  Customers = 'customers',
  Items = 'items',
  CompanyAge = 'companyAgeInYears',
}

export interface AchievementSummary {
  type: AchievementType;
  name: string;
  quantity: number;
}

export function Achievements() {
  const achievementClient = useMemo(() => new AchieveClient(), []);
  const [achievements, setAchievements] = useState<AchievementSummary[]>([]);
  const { t } = useTranslation(['salesPoint', 'achievement']);

  const getName = useCallback(
    (achievementType: AchievementType, value: number) => {
      switch (achievementType) {
        case AchievementType.Stores:
          return t('salesPoint:salesPoints', { count: value });
        case AchievementType.SmartWardrobes:
          return t('achievement:smartDrobes');
        case AchievementType.Customers:
          return t('achievement:customers');
        case AchievementType.Items:
          return t('achievement:items');
        case AchievementType.CompanyAge:
          return t('achievement:companyAgeInYears', { count: value });

        default:
          return '';
      }
    },
    [t]
  );

  const loadAchievements = useCallback(() => {
    achievementClient
      .getLaundryAchievement()
      .then((response) => {
        var achievementArray: AchievementSummary[] = [];

        for (const [key, value] of Object.entries(response)) {
          const achievementType = key as AchievementType;
          if (achievementType) {
            var achievement = {
              name: getName(achievementType, value),
              quantity: value,
              type: achievementType,
            } as AchievementSummary;

            achievementArray.push(achievement);
          } else {
            console.error('Not recognized achievement type: ' + key);
          }
        }

        achievementArray = achievementArray.sort((a, b) => a.quantity - b.quantity);
        setAchievements(achievementArray);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [achievementClient, getName]);

  useEffect(() => {
    loadAchievements();
  }, [loadAchievements]);

  const getIcon = (achievementType: AchievementType) => {
    switch (achievementType) {
      case AchievementType.Stores:
        return <PlaceRoundedIcon fontSize="large" />;
      case AchievementType.SmartWardrobes:
        return <BorderAllIcon fontSize="large" />;
      case AchievementType.Customers:
        return <PeopleIcon fontSize="large" />;
      case AchievementType.Items:
        return <LocalLaundryServiceOutlinedIcon fontSize="large" />;
      case AchievementType.CompanyAge:
        return <CalendarMonthIcon fontSize="large" />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('achievement:achievements')} />
      <BreadcrumbsBar paths={[{ path: AchievementsRoute }]} />
      {!achievements && <Linear />}
      <Grid
        sx={{
          overflow: 'auto',
          paddingTop: '0px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px',
          padding: { xs: '10px', sm: '20px' },
        }}
      >
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 1, md: 2, lg: 3 }}
          className={appStyleMode === AppStyle.Mobile && 'achievements'}
        >
          {achievements?.map((achievement, index) => {
            return (
              <Grid size={1} key={'achievement_' + index}>
                <AchievementItem
                  title={achievement.name}
                  value={achievement.quantity}
                  icon={getIcon(achievement.type)}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
}
